import type { FeatureContext } from './featureContext';
import { initWardrobe, type WardrobeLook } from '../wardrobe';
import { initNotifications } from '../notifications';
import { SplashPreview } from '../render/splashPreview';
import { CosmeticIconRenderer } from '../render/cosmeticIcon';
import { hueCss, hueGradientCss } from '../render/huePalette';

/** Injectable seams for unit tests; defaults are the real modules. */
export interface WardrobeInstallDeps {
  initWardrobe?: typeof initWardrobe;
  initNotifications?: typeof initNotifications;
}

/**
 * The wardrobe / dressing panel (Cosmetic Progression Slice E, ADR-0050) and the
 * unlock-notifications bell (ADR-0124): dress from the cosmetics you own,
 * server-validated and persisted. The wardrobe's live preview reuses the real
 * PlayerSprite stack (SplashPreview), mounted lazily on first open and kept alive
 * across re-opens; the owned collection arrives via the server's `Collection`
 * push (which the notifications feed diffs client-side - no new server channel);
 * the worn outfit and body are reconciled from the authoritative snapshot (the
 * record is the source of truth, ADR-0044). Both panels join the toolbar's
 * one-open-at-a-time group (ADR-0133). Three ways in share the one open path:
 * the toolbar button, the `/dress` chat command, and the paperdoll's self-only
 * `Dress` action (the `openWardrobe` hub event - double-clicking self now opens
 * the paperdoll, PRD #629, superseding ADR-0073's direct wardrobe open).
 */
export function installWardrobe(ctx: FeatureContext, deps: WardrobeInstallDeps = {}): void {
  const { bodyFrames, equipFrames, hueTable, huePalette, atlasStore } = ctx.assets;
  const wardrobePreviewMount = ctx.ui.getElementById('wardrobe-preview')!;
  const cosmeticIcons = new CosmeticIconRenderer(bodyFrames, equipFrames, hueTable, ctx.ui, atlasStore);
  let wardrobePreview: SplashPreview | undefined;
  let wardrobeLook: WardrobeLook | undefined;
  wardrobePreviewMount.addEventListener('wheel', (event) => {
    if (!wardrobePreview || event.deltaY === 0) return;
    event.preventDefault();
    wardrobePreview.rotate(event.deltaY > 0 ? 1 : -1);
  }, { passive: false });
  const wardrobe = (deps.initWardrobe ?? initWardrobe)({
    hueCss: (hue) => hueCss(huePalette, hue),
    hueGradientCss: (hue) => hueGradientCss(huePalette, hue),
    onEquip: (outfit) => ctx.client()?.equip(outfit),
    onAutoEquip: (enabled) => ctx.client()?.setAutoEquip(enabled),
    onUnlockFavorite: () => ctx.client()?.unlockWardrobeFavorite(),
    onFavoriteUnlocked: () => ctx.audio.play({ sound: 'collection-positive', volume: 1 }),
    renderFavoritePreview: (mount, look) => {
      void cosmeticIcons.renderOutfit(look).then((canvas) => {
        if (canvas) mount.replaceChildren(canvas);
      }).catch(() => {});
    },
    onSaveFavorite: (slot, outfit) => ctx.client()?.saveWardrobeFavorite(slot, outfit),
    onOpenChange: (open) => ctx.toolbar.setOpen('wardrobe', open),
    onPreview: (look) => {
      wardrobeLook = look;
      wardrobePreview?.update(look);
    },
    onOpen: () => {
      if (wardrobePreview) return;
      void SplashPreview.mount(wardrobePreviewMount, bodyFrames, equipFrames, hueTable, atlasStore, 280).then(
        (p) => {
          wardrobePreview = p;
          if (wardrobeLook) p.update(wardrobeLook);
        },
      );
    },
  });
  ctx.toolbar.register('wardrobe', () => wardrobe.close());
  ctx.actions.register('wardrobe', { down: () => wardrobe.toggle() });

  // The unlock notifications feed (ADR-0124): the toolbar bell + dropdown listing the
  // cosmetics unlocked this session. Its feed is a *client-side diff* of the same
  // collection pushes the wardrobe reads, so it needs no new server channel. The
  // per-item picture reuses the wardrobe/splash art path via a shared offscreen icon
  // renderer (one WebGL context, cached per {item, hue}); the hue swatch reuses the
  // same hueCss the wardrobe swatches use.
  const notifications = (deps.initNotifications ?? initNotifications)({
    hueCss: (hue) => hueCss(huePalette, hue),
    onOpenChange: (open) => ctx.toolbar.setOpen('notifications', open),
    renderIcon: (item, hue) => {
      // Return a placeholder immediately; fill it with the baked-art canvas once the
      // (async, one-time-per-pair) render resolves, so the DOM row stays synchronous.
      const box = ctx.ui.createElement('span');
      void cosmeticIcons
        .render(item, hue)
        .then((canvas) => {
          if (canvas) box.replaceChildren(canvas);
        })
        .catch(() => {
          // A render failure (e.g. no WebGL context) leaves the placeholder empty
          // rather than becoming an unhandled rejection - skip, don't crash.
        });
      return box;
    },
  });
  ctx.toolbar.register('notifications', () => notifications.close());

  // The earned collection (Slice E, ADR-0050): pushed off the snapshot stream. It
  // arrives on login (after the retroactive grant) and again whenever a fresh unlock
  // grows it. The notifications feed reads the same push: its first call seeds a
  // silent baseline (the login grant is not "new"), and each later growth becomes an
  // unlock notification.
  ctx.events.on('collection', (unlocked) => {
    wardrobe.setCollection(unlocked);
    notifications.setCollection(unlocked);
  });

  // The persisted auto-equip preference (ADR-0123): pushed off-snapshot on login,
  // so the wardrobe checkbox opens reflecting the account's saved choice. Set
  // without echoing back up as a fresh toggle (see wardrobe.setAutoEquip).
  ctx.events.on('autoEquipState', (enabled) => wardrobe.setAutoEquip(enabled));
  ctx.events.on('wardrobeFavoritesState', (state) =>
    wardrobe.setFavorites(state.reserved, state.favorites),
  );
  ctx.events.on('goldUpdate', (gold) => wardrobe.setGold(gold));

  // Reconcile the wardrobe's body + worn outfit to the authoritative entity (the
  // record is the source of truth, ADR-0044). Both setters no-op when unchanged,
  // so the 10Hz snapshot stream (ADR-0163) never thrashes the panel.
  ctx.events.on('snapshot', (world) => {
    const playerId = ctx.playerId();
    const identity = ctx.identity();
    if (playerId === undefined || !identity) return;
    const me = world.get(playerId)?.renderable;
    if (me) {
      wardrobe.setBody(me.body ?? identity.sex, me.bodyHue ?? identity.hue);
      wardrobe.setOutfit((me.equipment ?? []).map((it) => ({ item: it.id, hue: it.hue ?? 0 })));
    }
  });

  // `/dress` is a client-side shortcut that opens the wardrobe rather than a spoken
  // line - intercepted before it reaches the say path (unlike `/top`, which the
  // server answers).
  ctx.chatCommands.intercept((text) => {
    if (text.trim().toLowerCase() !== '/dress') return false;
    wardrobe.toggle();
    return true;
  });

  // The paperdoll's self-only `Dress` action (PRD #629): a client-local hub event
  // the paperdoll installer emits, routed to the same wardrobe-open path the
  // toolbar button and `/dress` use. This replaces the direct own-character
  // double-click wiring - double-clicking self now opens the paperdoll (which
  // carries `Dress` as the short route in), superseding ADR-0073. `open()` (not
  // `toggle()`) keeps an already-open wardrobe open rather than closing it.
  ctx.events.on('openWardrobe', () => wardrobe.open());
}
